import React, { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Lock, Eye, Clock, TrendingUp, Activity, RefreshCw } from "lucide-react";
import { fetchAdminMedia, fetchAnalyticsDashboard, fetchAnalyticsToday } from "../lib/api";
import { mergeMedia } from "../lib/media";
import FormInboxes from "../components/admin/FormInboxes";
import { ButtonsCard, OutboundCard, ConversionsCard, FormFunnelCard, MediaStoriesCard, SOURCE_NAMES } from "../components/admin/AnalyticsInsights";
import useNoIndex from "../hooks/useNoIndex";

const COLORS = [
  "hsl(16,86%,49%)",
  "hsl(24,70%,55%)",
  "hsl(0,0%,65%)",
  "hsl(200,55%,55%)",
  "hsl(340,60%,55%)",
  "hsl(160,50%,50%)",
];

const fmt = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

const fmtDuration = (s) => {
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}m ${sec}s`;
};

const datePresets = [
  { label: "Today", days: 0 },
  { label: "7d", days: 6 },
  { label: "30d", days: 29 },
  { label: "90d", days: 89 },
];

const toDateStr = (d) => d.toISOString().slice(0, 10);

const AdminAnalytics = () => {
  useNoIndex();
  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem("mise-admin-token") || ""; }
    catch { return ""; }
  });
  const [unlocked, setUnlocked] = useState(!!token);
  const [tokenInput, setTokenInput] = useState("");

  const today = new Date();
  const [range, setRange] = useState({
    start: toDateStr(new Date(today.getTime() - 29 * 86400000)),
    end: toDateStr(today),
  });

  const applyPreset = useCallback((days) => {
    const now = new Date();
    setRange({
      start: days === 0 ? toDateStr(now) : toDateStr(new Date(now.getTime() - days * 86400000)),
      end: toDateStr(now),
    });
  }, []);

  const { data: dashboard, isLoading, error, refetch } = useQuery({
    queryKey: ["analytics-dashboard", range.start, range.end, token],
    queryFn: () => fetchAnalyticsDashboard(token, range.start, range.end),
    enabled: unlocked && !!token,
    staleTime: 60000,
    retry: false,
  });

  const { data: liveData } = useQuery({
    queryKey: ["analytics-today", token],
    queryFn: () => fetchAnalyticsToday(token),
    enabled: unlocked && !!token,
    refetchInterval: 30000,
    retry: false,
  });

  // Stories on /media: the ones built into the site plus published ones from /admin/media.
  const { data: adminMedia } = useQuery({
    queryKey: ["admin-media", token],
    queryFn: () => fetchAdminMedia(token),
    enabled: unlocked && !!token,
    staleTime: 300000,
    retry: false,
  });
  const stories = mergeMedia((adminMedia || []).filter((m) => m.published));

  useEffect(() => {
    if (error?.response?.status === 401) {
      setUnlocked(false);
      setToken("");
      try { sessionStorage.removeItem("mise-admin-token"); } catch {}
    }
  }, [error]);

  const handleUnlock = (e) => {
    e.preventDefault();
    const t = tokenInput.trim();
    if (!t) return;
    try { sessionStorage.setItem("mise-admin-token", t); } catch {}
    setToken(t);
    setUnlocked(true);
  };

  if (!unlocked) {
    return (
      <div className="mise-container pt-32 pb-20">
        <form onSubmit={handleUnlock} className="mise-card max-w-md mx-auto p-10 grid gap-5 text-center">
          <Lock className="mx-auto text-[#E8490F]" size={36} />
          <h2 className="text-2xl font-semibold">Analytics</h2>
          <p className="text-white/60 text-sm">Enter admin token to continue</p>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Admin token"
            className="bg-[#1a1a1a] border border-[#444444] focus:border-[#E8490F] outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30"
            autoFocus
          />
          <button type="submit" className="mise-pill-primary justify-center">
            Unlock
          </button>
        </form>
      </div>
    );
  }

  const t = dashboard?.totals || {};

  return (
    <div className="mise-container pt-10 pb-20">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <p className="mise-eyebrow">Admin</p>
          <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">Site Analytics</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {liveData && (
            <div className="flex items-center gap-2 text-sm text-white/60 mr-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
              </span>
              {liveData.active_sessions} active
            </div>
          )}
          {datePresets.map((p) => (
            <button
              key={p.label}
              onClick={() => applyPreset(p.days)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                range.start === toDateStr(new Date(today.getTime() - p.days * 86400000)) && range.end === toDateStr(today)
                  ? "bg-[#E8490F] text-white"
                  : "bg-[#242424] text-white/70 hover:text-white border border-[#444444]"
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => refetch()}
            className="p-2 rounded-lg bg-[#242424] border border-[#444444] text-white/70 hover:text-white transition-colors"
            title="Refresh"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      <FormInboxes token={token} />

      {isLoading && (
        <div className="grid gap-5 md:grid-cols-4 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="mise-card p-6 animate-pulse">
              <div className="h-3 w-20 bg-[#333] rounded mb-4" />
              <div className="h-8 w-24 bg-[#333] rounded" />
            </div>
          ))}
        </div>
      )}

      {dashboard && (
        <>
          {/* KPI Cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
            <KpiCard icon={Eye} label="Pageviews" value={fmt(t.pageviews || 0)} />
            <KpiCard icon={Activity} label="Sessions" value={fmt(t.sessions || 0)} />
            <KpiCard icon={Clock} label="Avg Duration" value={fmtDuration(t.avg_duration || 0)} />
            <KpiCard icon={TrendingUp} label="Bounce Rate" value={`${t.bounce_rate || 0}%`} />
          </div>

          {/* Traffic over time */}
          <div className="mise-card p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Traffic Over Time</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dashboard.timeline || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="date" tick={{ fill: "#999", fontSize: 12 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fill: "#999", fontSize: 12 }} />
                  <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #444", borderRadius: 8 }} />
                  <Legend />
                  <Line type="monotone" dataKey="pageviews" stroke="hsl(16,86%,49%)" strokeWidth={2} dot={false} name="Pageviews" />
                  <Line type="monotone" dataKey="sessions" stroke="hsl(200,55%,55%)" strokeWidth={2} dot={false} name="Sessions" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <ConversionsCard conversions={dashboard.conversions || []} timeline={dashboard.timeline || []} />

          <MediaStoriesCard
            stories={stories}
            mediaPages={dashboard.media_pages || []}
            outbound={dashboard.outbound || []}
          />

          {/* Two-col: Buttons + Outgoing traffic */}
          <div className="grid gap-6 lg:grid-cols-2 mb-6">
            <ButtonsCard clicks={dashboard.clicks || []} />
            <OutboundCard outbound={dashboard.outbound || []} />
          </div>

          {/* Two-col: Pages + Traffic Sources */}
          <div className="grid gap-6 lg:grid-cols-2 mb-6">
            {/* Top Pages */}
            <div className="mise-card p-6">
              <h3 className="text-lg font-semibold mb-4">Top Pages</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(dashboard.top_pages || []).slice(0, 10)} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis type="number" tick={{ fill: "#999", fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="page"
                      width={120}
                      tick={{ fill: "#ccc", fontSize: 11 }}
                      tickFormatter={(p) => p.length > 18 ? p.slice(0, 18) + "..." : p}
                    />
                    <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #444", borderRadius: 8 }} />
                    <Bar dataKey="views" fill="hsl(16,86%,49%)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Traffic Sources Donut */}
            <div className="mise-card p-6">
              <h3 className="text-lg font-semibold mb-4">Traffic Sources</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={dashboard.traffic_sources || []}
                      dataKey="count"
                      nameKey="source"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      label={({ source, percent }) => `${SOURCE_NAMES[source] || source} ${(percent * 100).toFixed(0)}%`}
                    >
                      {(dashboard.traffic_sources || []).map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #444", borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Two-col: Devices + Browsers */}
          <div className="grid gap-6 lg:grid-cols-2 mb-6">
            <div className="mise-card p-6">
              <h3 className="text-lg font-semibold mb-4">Devices</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={dashboard.devices || []}
                      dataKey="count"
                      nameKey="device"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ device, percent }) => `${device} ${(percent * 100).toFixed(0)}%`}
                    >
                      {(dashboard.devices || []).map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #444", borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="mise-card p-6">
              <h3 className="text-lg font-semibold mb-4">Browsers</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dashboard.browsers || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis type="number" tick={{ fill: "#999", fontSize: 12 }} />
                    <YAxis type="category" dataKey="browser" width={80} tick={{ fill: "#ccc", fontSize: 12 }} />
                    <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #444", borderRadius: 8 }} />
                    <Bar dataKey="count" fill="hsl(200,55%,55%)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Scroll Depth */}
          {(dashboard.scroll_depth || []).length > 0 && (
            <div className="mise-card p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Scroll Depth (Latest Day)</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dashboard.scroll_depth.slice(0, 10)} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fill: "#999", fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                    <YAxis
                      type="category"
                      dataKey="page"
                      width={120}
                      tick={{ fill: "#ccc", fontSize: 11 }}
                      tickFormatter={(p) => p.length > 18 ? p.slice(0, 18) + "..." : p}
                    />
                    <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #444", borderRadius: 8 }} formatter={(v) => `${v}%`} />
                    <Bar dataKey="avg_depth" fill="hsl(24,70%,55%)" radius={[0, 4, 4, 0]} name="Avg %" />
                    <Bar dataKey="max_depth" fill="hsl(16,86%,49%)" radius={[0, 4, 4, 0]} name="Max %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <FormFunnelCard formActions={dashboard.form_actions || []} />

          {/* Two-col: Referrers + Languages */}
          <div className="grid gap-6 lg:grid-cols-2 mb-6">
            <div className="mise-card p-6">
              <h3 className="text-lg font-semibold mb-4">Top Referrers</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#333]">
                      <th className="text-left py-2 text-white/50 font-medium">Domain</th>
                      <th className="text-right py-2 text-white/50 font-medium">Visits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dashboard.referrers || []).slice(0, 15).map((r, i) => (
                      <tr key={i} className="border-b border-[#222]">
                        <td className="py-2 text-white/80">{r.domain}</td>
                        <td className="py-2 text-right text-white font-medium">{r.count}</td>
                      </tr>
                    ))}
                    {(dashboard.referrers || []).length === 0 && (
                      <tr><td colSpan={2} className="py-4 text-center text-white/40">No referrer data yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mise-card p-6">
              <h3 className="text-lg font-semibold mb-4">Languages</h3>
              {(dashboard.languages || []).length === 0 ? (
                <p className="text-sm text-white/40">No language data yet</p>
              ) : (
                <div className="flex gap-4 flex-wrap">
                  {dashboard.languages.map((l) => (
                    <div key={l.lang} className="bg-[#1a1a1a] border border-[#333] rounded-xl px-5 py-3 text-center">
                      <p className="text-2xl font-semibold">{l.lang?.toUpperCase()}</p>
                      <p className="text-sm text-white/60 mt-1">{l.count} views</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        </>
      )}

      {error && !isLoading && (
        <div className="mise-card p-10 text-center">
          <p className="text-white/60">Failed to load analytics data.</p>
          <button onClick={() => refetch()} className="mise-pill-primary mt-4">Retry</button>
        </div>
      )}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value }) => (
  <div className="mise-card p-5 flex items-center gap-4">
    <div className="p-2.5 rounded-xl bg-[#E8490F]/10">
      <Icon size={20} className="text-[#E8490F]" />
    </div>
    <div>
      <p className="text-xs text-white/50 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-semibold tracking-tight mt-0.5">{value}</p>
    </div>
  </div>
);

export default AdminAnalytics;
